import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { DisplayS, LabelL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LastOwnedCardsBySo5Competition } from '@sorare/marketplace/src/components/player/LastOwnedCardsBySo5Competition';
import { Header as ScoutingCenterHeaderGeneric } from '@sorare/marketplace/src/searchCommonPlayer/Header';

import { ScoutingCenterBySo5Competition } from 'components/scoutingCenter/ScoutingCenterBySo5Competition';

import type {
  ScoutingCenterHeader_competition,
  ScoutingCenterHeader_so5Competition,
  ScoutingCenterHeader_so5LeaderboardGroup,
} from './__generated__/index.graphql';
import bg from './assets/background.webp';
import proIcon from './assets/pro.svg';

type Props = {
  so5Competition: Nullable<ScoutingCenterHeader_so5Competition>;
  competitions?: ScoutingCenterHeader_competition[];
  so5LeaderboardGroupInterface?: ScoutingCenterHeader_so5LeaderboardGroup;
  loading?: boolean;
};

const So5LeaderboardGroupLogo = styled.img`
  display: none;
  @media ${desktopAndAbove} {
    display: block;
    height: calc(12 * var(--unit));
    width: auto;
  }
`;

const Row = styled(SBHorizontal)`
  flex-wrap: wrap;
  width: 100%;
  align-items: flex-start;
`;

const FlexWrap = styled(Horizontal)`
  flex-wrap: wrap;
  gap: var(--unit);
`;

const Title = styled(Vertical)`
  flex-grow: 1;
  display: none;
  @media ${desktopAndAbove} {
    display: flex;
  }
`;

const ExponentStarball = styled(StarBall)`
  width: var(--intermediate-unit);
  height: var(--intermediate-unit);
  vertical-align: text-top;
`;

const topLeaguesMessages = defineMessages<string>({
  'seasonal-champions': {
    id: 'scoutingCenter.topLeagues.champions',
    defaultMessage: 'Top 5 leagues',
  },
  'seasonal-challengers': {
    id: 'scoutingCenter.topLeagues.challengers',
    defaultMessage: 'Top 6-14 leagues',
  },
  'seasonal-contenders': {
    id: 'scoutingCenter.topLeagues.contenders',
    defaultMessage: 'Top 15-28 leagues',
  },
});

export const ScoutingCenterHeader = ({
  so5Competition,
  so5LeaderboardGroupInterface,
  competitions,
  loading,
}: Props) => {
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const { generateSportContextPath } = useSportContext();

  const {
    displayName: so5CompetitionDisplayName,
    headerBackgroundUrl: so5CompetitionHeaderBackgroundUrl,
    headerBackgroundDesktopUrl: so5CompetitionHeaderBackgroundDesktopUrl,
    iconUrl,
    scoutable,
    playableSo5LeaderboardGroupInterfaces,
  } = so5Competition || {};

  const {
    headerBackgroundUrl: so5LeaderboardGroupHeaderBackgroundUrl,
    displayName: so5LeaderboardGroupDisplayName,
  } = so5LeaderboardGroupInterface || {};

  const { headerBackgroundUrl, headerBackgroundDesktopUrl, displayName } =
    (() => {
      if (!scoutable && so5LeaderboardGroupInterface)
        return {
          headerBackgroundUrl: so5LeaderboardGroupHeaderBackgroundUrl,
          headerBackgroundDesktopUrl: so5LeaderboardGroupHeaderBackgroundUrl,
          displayName: so5LeaderboardGroupDisplayName,
        };
      return {
        headerBackgroundUrl: so5CompetitionHeaderBackgroundUrl,
        headerBackgroundDesktopUrl: so5CompetitionHeaderBackgroundDesktopUrl,
        displayName: so5CompetitionDisplayName,
      };
    })();

  const hasDomesticLeagues = (competitions?.length || 0) > 1;

  const backgroundUrl =
    (isLaptopOrAbove && headerBackgroundDesktopUrl) || headerBackgroundUrl;

  const nextPlayableSo5LeaderboardGroupInterface =
    so5LeaderboardGroupInterface || playableSo5LeaderboardGroupInterfaces?.[0];

  return (
    <ScoutingCenterHeaderGeneric
      backgroundImage={loading ? '' : backgroundUrl || bg}
      fixed
      tabs={<ScoutingCenterBySo5Competition />}
    >
      <Row>
        {!!so5Competition && (
          <FlexWrap>
            <LastOwnedCardsBySo5Competition so5Competition={so5Competition} />
          </FlexWrap>
        )}
        {!!nextPlayableSo5LeaderboardGroupInterface && (
          <Button
            size="compact"
            color="quaternary"
            blur
            startIcon={<img src={proIcon} alt="" />}
            to={generateSportContextPath(
              ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION,
              {
                params: {
                  fixture:
                    nextPlayableSo5LeaderboardGroupInterface.so5Fixture.slug,
                  eventType:
                    nextPlayableSo5LeaderboardGroupInterface.so5Fixture.type.toLowerCase(),
                  competition: nextPlayableSo5LeaderboardGroupInterface.slug,
                },
              }
            )}
          >
            <FormattedMessage {...fantasy.setLineup} />
          </Button>
        )}
      </Row>
      {loading && isLaptopOrAbove ? (
        <LoadingIndicator />
      ) : (
        <>
          {(!!hasDomesticLeagues || !so5Competition) && (
            <Title center>
              <DisplayS>
                {displayName ? (
                  <>
                    {displayName} <ExponentStarball />
                  </>
                ) : (
                  <FormattedMessage
                    id="scoutingCenter.header.title.all"
                    defaultMessage="All players"
                  />
                )}
              </DisplayS>
              {!!so5Competition &&
                !!topLeaguesMessages?.[so5Competition.slug] && (
                  <LabelL color="var(--c-nd-600)">
                    <FormattedMessage
                      {...topLeaguesMessages[so5Competition?.slug]}
                    />
                  </LabelL>
                )}
              {!so5Competition && (
                <LabelL color="var(--c-nd-600)">
                  <FormattedMessage
                    id="scoutingCenter.header.subtitle.all"
                    defaultMessage="Scout them all"
                  />
                </LabelL>
              )}
            </Title>
          )}
          {!hasDomesticLeagues && !!iconUrl && (
            <So5LeaderboardGroupLogo
              src={iconUrl}
              alt={`${displayName}-logo`}
            />
          )}
        </>
      )}
    </ScoutingCenterHeaderGeneric>
  );
};

const so5LeaderboardGroupFragment = gql`
  fragment ScoutingCenterHeader_so5LeaderboardGroup on So5LeaderboardGroupInterface {
    slug
    displayName
    so5Fixture {
      slug
      type
    }
    headerBackgroundUrl(seasonality: IN_SEASON)
  }
` as TypedDocumentNode<ScoutingCenterHeader_so5LeaderboardGroup>;

ScoutingCenterHeader.fragments = {
  so5Competition: gql`
    fragment ScoutingCenterHeader_so5Competition on So5Competition {
      slug
      displayName
      headerBackgroundUrl
      headerBackgroundDesktopUrl
      iconUrl
      scoutable
      playableSo5LeaderboardGroupInterfaces {
        slug
        so5Fixture {
          slug
          type
        }
        ...ScoutingCenterHeader_so5LeaderboardGroup
      }
      ...LastOwnedCardsBySo5Competition_so5Competition
    }
    ${so5LeaderboardGroupFragment}
    ${LastOwnedCardsBySo5Competition.fragments.so5Competition}
  ` as TypedDocumentNode<ScoutingCenterHeader_so5Competition>,
  competition: gql`
    fragment ScoutingCenterHeader_competition on Competition {
      slug
    }
  ` as TypedDocumentNode<ScoutingCenterHeader_competition>,
  so5LeaderboardGroup: so5LeaderboardGroupFragment,
};
